import { createContext, useContext } from "react";

/**
 * Identifies which host is running the lending module and, by extension, which
 * credential family the lending API client authenticates with.
 *
 * `lending_session` is the standalone app: the `lending_session` cookie plus a
 * synchronizer CSRF token fetched from the lending API.
 *
 * `digital_banking` is lending hosted inside Narmi Online Banking, where the user is
 * already authenticated by the Narmi Django session. The API dispatches on the
 * `X-Lending-Auth-Mode` header rather than trying both credentials, because inside Online
 * Banking both cookies can be present at once and chaining would let whichever
 * authenticator ran first silently decide the principal.
 *
 * The host is the only thing that knows this value; components stay unaware.
 */
export type LendingAuthMode = "lending_session" | "digital_banking";

export interface LendingHostContextValue {
  authMode: LendingAuthMode;
  /**
   * Host-owned logout, for hosts (e.g. Online Banking) whose session backs the
   * whole shell — the lending module must not own that logout or its redirect.
   * Standalone lending omits it and tears down its own sessions instead.
   */
  onLogout?: () => void;
}

export const LendingHostContext = createContext<LendingHostContextValue>({
  authMode: "lending_session",
});

export function useLendingHost(): LendingHostContextValue {
  return useContext(LendingHostContext);
}
